import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Button,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { airdropService } from '../services/airdrop';
import { FaCheckCircle, FaExclamationTriangle, FaClock, FaSync } from 'react-icons/fa';

const AirdropHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const toast = useToast();

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await airdropService.getTransactionLog();
      setTransactions(data);
    } catch (err) {
      setError(err.message);
      toast({
        title: 'Error fetching transactions',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // Set up polling every 30 seconds
    const interval = setInterval(fetchTransactions, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <Icon as={FaCheckCircle} color="green.500" />;
      case 'failed':
        return <Icon as={FaExclamationTriangle} color="red.500" />;
      case 'pending':
        return <Icon as={FaClock} color="yellow.500" />;
      default:
        return null;
    }
  };

  const formatDate = (timestamp) => {
    return new Date(Number(timestamp)).toLocaleString();
  };

  const formatAmount = (amount) => {
    return `${(Number(amount) / 1e9).toFixed(4)} SOL`;
  };

  const getTransactionStats = () => {
    const total = transactions.length;
    const completed = transactions.filter(t => t.status === 'completed').length;
    const failed = transactions.filter(t => t.status === 'failed').length;
    const pending = transactions.filter(t => t.status === 'pending').length;
    const totalAmount = transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + Number(t.amount), 0) / 1e9;

    return { total, completed, failed, pending, totalAmount };
  };

  const stats = getTransactionStats();

  if (loading && transactions.length === 0) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading transaction history...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Flex justify="space-between" align="center">
        <Heading size="lg">Airdrop Transaction History</Heading>
        <Button
          leftIcon={<FaSync />}
          onClick={fetchTransactions}
          isLoading={loading}
          colorScheme="blue"
        >
          Refresh
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <Stat>
          <StatLabel>Total Transactions</StatLabel>
          <StatNumber>{stats.total}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Completed</StatLabel>
          <StatNumber color="green.500">{stats.completed}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Failed</StatLabel>
          <StatNumber color="red.500">{stats.failed}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Total Amount</StatLabel>
          <StatNumber>{stats.totalAmount.toFixed(4)} SOL</StatNumber>
        </Stat>
      </SimpleGrid>

      {error && (
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {transactions.length === 0 ? (
        <Alert status="info">
          <AlertIcon />
          <AlertTitle>No Transactions</AlertTitle>
          <AlertDescription>
            No airdrop transactions have been recorded yet.
          </AlertDescription>
        </Alert>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Time</Th>
                <Th>Recipient</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Transaction Hash</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.map((tx, index) => (
                <Tr key={index}>
                  <Td>{formatDate(tx.timestamp)}</Td>
                  <Td>
                    <Text fontFamily="monospace">
                      {tx.recipient.slice(0, 4)}...{tx.recipient.slice(-4)}
                    </Text>
                  </Td>
                  <Td>{formatAmount(tx.amount)}</Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(tx.status)}>
                      {getStatusIcon(tx.status)} {tx.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontFamily="monospace" fontSize="sm">
                      {tx.txHash.slice(0, 8)}...{tx.txHash.slice(-8)}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  );
};

export default AirdropHistory; 